import React, { useState, useEffect, useRef, useContext } from 'react';
import './firstTab.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { UserContext } from '../../Context/UserContext';

function FirstTab() {
    const navigate = useNavigate();
    // Default theme is 'dark'
    const [theme, setTheme] = useState('light');
    const [showThemeDropdown, setShowThemeDropdown] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [workflowSettings, setWorkflowSettings] = useState({});
    const [visibleButtons, setVisibleButtons] = useState([]);
    const [menuTimings, setMenuTimings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { baseUrl, getHeaders } = useContext(UserContext);
    const dropdownRef = useRef(null);

    // Button Data Definition
    const buttonData = [
        { label: 'Take Away', icon: 'fa-bag-shopping', path: '/frontpage', type: 'Take Away', description: 'Order & pick up', workflowKey: 'Takeaway' },
        { label: 'Dine In', icon: 'fa-utensils', path: '/table', type: 'Dine In', description: 'Enjoy your meal', workflowKey: 'Dine In' },
        { label: 'Online Delivery', icon: 'fa-truck', path: '/frontpage', type: 'Online Delivery', description: 'Doorstep delivery', workflowKey: 'Online Delivery' },
        { label: 'Guest Kiosk', icon: 'fa-desktop', path: '/kiosk', type: 'Guest Kiosk', description: 'Self Ordering Kiosk', workflowKey: 'Guest Kiosk' },
        { label: 'Booking', icon: 'fa-calendar-check', path: '/booking', type: 'Booking', description: 'Reserve a table', workflowKey: 'Booking' },
        { label: 'Trip Report', icon: 'fa-file-invoice', path: '/trip-report', type: 'Trip Report', description: 'Delivery reports', workflowKey: 'TripReport' },
        { label: 'POS Balance', icon: 'fa-wallet', path: '/pos-balance', type: 'POS Balance', description: 'Check system balance', workflowKey: 'POSBalance' },
        { label: 'Delivery Panel', icon: 'fa-motorcycle', path: '/delivery-panel', type: 'Delivery Panel', description: 'Delivery portal', workflowKey: 'Delivery Panel' },
        { label: 'Voice', icon: 'fa-volume-high', path: '/voice', type: 'Voice', description: 'Voice commands', workflowKey: 'Voice' },
        { label: 'Order Tracking', icon: 'fa-location-crosshairs', path: '/track', type: 'Order Tracking', description: 'Live delivery tracking', workflowKey: 'Order Tracking' },
        { label: 'Offers', icon: 'fa-tags', path: '/offers', type: 'Offers', description: 'Combo Offers & Discounts', workflowKey: 'Offers' },
        { label: 'Menus', icon: 'fa-utensils', path: '/menus', type: 'Menus', description: 'View Menu Board', workflowKey: 'Menus' },
    ];
    const handleNavigation = (path, orderType, timingId = null) => {
        localStorage.setItem('selectedOrderType', orderType);
        localStorage.setItem('isOrderTypeSelected', 'true');
        if (timingId) localStorage.setItem('selectedTimingId', timingId);
        else localStorage.removeItem('selectedTimingId');
        
        navigate(path, { state: { orderType, timingId } });
    };

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem("user");
        localStorage.removeItem("active_company");
        localStorage.removeItem("active_branch");
        localStorage.removeItem("pos_profile");
        localStorage.removeItem("selectedOrderType");
        localStorage.removeItem("isOrderTypeSelected");
        // Clear all redirect flags on logout
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('hasRedirectedToOpening_')) {
                sessionStorage.removeItem(key);
            }
        });
        navigate("/login");
    };

    const cancelLogout = () => {
        setShowLogoutModal(false);
    };

    const handleThemeSelect = (selectedTheme) => {
        setTheme(selectedTheme);
        setShowThemeDropdown(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowThemeDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // Fetch Workflow Visibility Settings
    const fetchWorkflowData = async () => {
        setIsLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user')) || {};
            const activeComp = localStorage.getItem('active_company') || storedUser.company || storedUser.company_name;
            const activeBranch = localStorage.getItem('active_branch') || storedUser.branch || storedUser.branch_name;

            if (!activeComp) {
                setIsLoading(false);
                setVisibleButtons(buttonData);
                return;
            }

            const params = { company: activeComp };
            if (activeBranch && activeBranch !== 'All Branches') params.branch = activeBranch;

            console.log(`[FirstTab Sync] Fetching for ${activeComp} / ${activeBranch}`);
            const response = await axios.get(`${baseUrl}/api/workflow-visibility`, {
                params,
                headers: getHeaders ? getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeComp) : {}
            });
            const settings = response.data.settings || {};
            console.log("[FirstTab Sync] Received Settings Keys:", Object.keys(settings));
            console.log("[FirstTab Sync] Takeaway Setting:", settings['Takeaway']);
            setWorkflowSettings(settings);

            const coreKeys = ['Takeaway', 'Dine In', 'Online Delivery'];
            const hasAnyCoreConfig = coreKeys.some(key => settings[key] !== undefined);
            const hasAnyGlobalConfig = settings.global_modules && Object.keys(settings.global_modules).length > 0;
            const hasAnyConfigAtAll = hasAnyCoreConfig || hasAnyGlobalConfig;

            console.log(`[FirstTab] Filtering for ${activeComp}/${activeBranch}. hasAnyConfigAtAll=${hasAnyConfigAtAll}`);

            const filtered = buttonData.filter(btn => {
                const category = btn.workflowKey;
                const g = settings.global_modules;

                // If NO config at all exists for this company, show everything (First Run)
                if (!hasAnyConfigAtAll) return true;

                // 1. Core experiences (Takeaway, Dine In, Online Delivery)
                if (coreKeys.includes(category)) {
                    const s = settings[category];

                    // Strict Whitelist: Only show if explicitly enabled
                    if (!s || s.enabled !== true) return false;

                    // Cross-check with Global Module Suppression
                    if (category === 'Dine In' && g?.table_management?.enabled !== true) return false;
                    if (['Takeaway', 'Online Delivery'].includes(category) && g?.pos_billing?.enabled !== true) return false;

                    return true;
                }

                // 2. Extra features (Booking, TripReport, POSBalance, Voice)
                // Strict visibility mapping to global modules
                if (category === "Booking") {
                    return g?.table_management?.enabled === true && g?.table_management?.pages?.Booking === true;
                }
                if (category === "TripReport") {
                    return g?.reports?.enabled === true && g?.reports?.pages?.TripReport === true;
                }
                if (category === "POSBalance") {
                    return g?.pos_billing?.enabled === true && g?.pos_billing?.pages?.POSBalance === true;
                }
                if (category === "Voice") {
                    return g?.notifications?.enabled === true && g?.notifications?.pages?.VoiceSupport === true;
                }
                if (category === "Delivery Panel") {
                    return true; // You could map this to a specific workflow later
                }
                if (category === "Guest Kiosk") {
                    return true;
                }
                if (category === "Order Tracking") {
                    return g?.tracking?.enabled === true && g?.tracking?.pages?.LiveTracking === true;
                }
                if (category === "Offers") {
                    return g?.inventory?.enabled !== false && g?.inventory?.pages?.OffersDisplay !== false;
                }
                if (category === "ActivePlans" || category === "Menus") {
                    return g?.inventory?.enabled === true && g?.inventory?.pages?.ActivePlans === true;
                }

                return false;
            });

            setVisibleButtons(filtered);
        } catch (error) {
            console.error("Error fetching workflow settings:", error);
            setVisibleButtons(buttonData);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchTimings = async () => {
            try {
                const res = await axios.get(`${baseUrl}/api/menu_timings/active`, { headers: getHeaders() });
                setMenuTimings(res.data || []);
            } catch (error) {
                console.error("Error fetching menu timings:", error);
            }
        };
        fetchTimings();
        fetchWorkflowData();
        window.addEventListener('companyChange', fetchWorkflowData);
        window.addEventListener('visibilityChange', fetchWorkflowData);
        return () => {
            window.removeEventListener('companyChange', fetchWorkflowData);
            window.removeEventListener('visibilityChange', fetchWorkflowData);
        };
    }, []);

    // Nuclear Redirection Logic: Ensure a session is active before allowing access to the Home dashboard
    useEffect(() => {
        const verifySessionAndWorkflow = async () => {
            if (isLoading) return;

            try {
                const storedUser = JSON.parse(localStorage.getItem('user')) || {};
                const activeComp = localStorage.getItem('active_company') || storedUser.company || storedUser.company_name;
                const activeBranch = localStorage.getItem('active_branch') || storedUser.branch || storedUser.branch_name;

                const workflowRes = await axios.get(`${baseUrl || ''}/api/workflow-visibility`, {
                    params: {
                        company: activeComp,
                        branch: activeBranch
                    },
                    headers: getHeaders ? getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeComp) : {}
                });
                const w = workflowRes.data.settings || {};
                const g = w.global_modules;
                const isOpeningEntryEnabled = g?.pos_billing?.enabled === true && g?.pos_billing?.pages?.OpeningEntry === true;

                console.log("[FirstTab SessionCheck] Opening Entry Enabled:", isOpeningEntryEnabled);
                if (!isOpeningEntryEnabled) return; // Requirement is disabled, allow access

                // 2. Requirement is enabled, now check if we have an active session
                const checkUrl = `${baseUrl || ''}/api/get_pos_opening_entries`;
                const userObj = JSON.parse(localStorage.getItem('user')) || {};
                const openingRes = await axios.post(checkUrl, {
                    pos_profile: localStorage.getItem('pos_profile') || "POS-001",
                    userId: userObj.email || userObj.username,
                    role: userObj.role
                }, {
                    headers: {
                        ...(getHeaders ? getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeComp) : {}),
                        'X-Company-Name': activeComp || 'All',
                        'X-Branch-Name': activeBranch || 'All'
                    }
                });

                const userIdentifier = userObj.username || (userObj.email ? userObj.email.split('@')[0] : 'Guest');
                const activeEntries = (openingRes.data?.message || []).filter(e =>
                    e.status === 'Open' && e.user === userIdentifier
                );
                console.log(`[FirstTab SessionCheck] Active Sessions found for ${userIdentifier}:`, activeEntries.length);

                if (activeEntries.length === 0) {
                    // Check if this user's role actually has permission to view Opening Entry!
                    let isOpeningAllowed = false;
                    try {
                        const permHeaders = getHeaders 
                            ? getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeComp)
                            : {};
                        // Explicitly add company & branch headers so the backend can scope the lookup correctly
                        if (activeComp) permHeaders['X-Company-Name'] = activeComp;
                        if (activeBranch && activeBranch !== 'All Branches') permHeaders['X-Branch-Name'] = activeBranch;

                        const rolePermRes = await axios.get(
                            `${baseUrl || ''}/api/role-permissions?role=${encodeURIComponent(userObj.role)}&t=${Date.now()}`,
                            { headers: permHeaders }
                        );
                        const perms = rolePermRes.data.permissions || [];
                        const hasAllPerm = perms.some(p => p.pageId === 'all');
                        const openingPerm = perms.find(p => p.pageId === 'opening');

                        // Check for admin role directly (admins always get access)
                        const roleLower = (userObj.role || '').toLowerCase().replace(/[\s_]/g, '');
                        const isAdminRole = roleLower.includes('admin') || roleLower.includes('manager') || 
                                           roleLower.includes('owner') || roleLower.includes('super');

                        if (isAdminRole || hasAllPerm) {
                            isOpeningAllowed = true;
                        } else if (openingPerm) {
                            // FIX: canRead can be stored as boolean true, number 1, or string "true"/"1"
                            // Use truthy check to handle all cases from the database
                            const canRead = openingPerm.canRead;
                            isOpeningAllowed = canRead === true || canRead === 1 || canRead === 'true' || canRead === '1' || !!canRead;
                        }

                        console.log("[FirstTab SessionCheck] Opening perm:", openingPerm, "isOpeningAllowed:", isOpeningAllowed);
                    } catch (permErr) {
                        console.error("HomeDashboard: Failed to fetch role permissions", permErr);
                        // On error, allow redirect to opening entry as a safe fallback
                        isOpeningAllowed = true;
                    }

                    if (!isOpeningAllowed) {
                        console.log("HomeDashboard: User lacks permission for Opening Entry. Skipping redirect to /opening-entry -> Staying on /home.");
                        return; // Bypass forced redirect
                    }

                    console.warn("HomeDashboard: No active session found. Redirecting to /opening-entry");
                    navigate("/opening-entry", { replace: true });
                }
            } catch (err) {
                console.error("HomeDashboard: Session verification failed", err);
            }
        };

        verifySessionAndWorkflow();
    }, [isLoading, navigate, baseUrl]);

    // Keyboard Navigation for Action Cards
    useEffect(() => {
        const isKeyboardShortcutsEnabled = localStorage.getItem("isKeyboardShortcutsEnabled") === "true";
        if (!isKeyboardShortcutsEnabled) return;

        const handleKeyDown = (e) => {
            const getCards = () => Array.from(document.querySelectorAll('.action-card'));
            const activeEl = document.activeElement;
            const isCardActive = activeEl && activeEl.classList.contains('action-card');

            switch (e.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    e.preventDefault();
                    if (isCardActive) {
                        const cards = getCards();
                        const idx = cards.indexOf(activeEl);
                        if (idx > 0) cards[idx - 1].focus();
                    } else {
                        const cards = getCards();
                        if (cards.length > 0) cards[cards.length - 1].focus();
                    }
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    e.preventDefault();
                    if (isCardActive) {
                        const cards = getCards();
                        const idx = cards.indexOf(activeEl);
                        if (idx >= 0 && idx < cards.length - 1) cards[idx + 1].focus();
                    } else {
                        const cards = getCards();
                        if (cards.length > 0) cards[0].focus();
                    }
                    break;
                case 'Enter':
                    if (isCardActive) {
                        e.preventDefault();
                        activeEl.click();
                    }
                    break;
                case 'Backspace':
                    if (activeEl.tagName !== 'INPUT' && activeEl.tagName !== 'TEXTAREA') {
                        e.preventDefault();
                        navigate(-1);
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);

    useEffect(() => {
        document.body.className = `theme-${theme}`;
        return () => {
            document.body.className = '';
        };
    }, [theme]);

    return (
        <div className={`first-tab-container theme-${theme}`}>
            <div className="top-right-section">
                <div className="theme-wrapper" ref={dropdownRef}>
                    <button
                        className={`nav-icon-button theme-toggle-button ${showThemeDropdown ? 'active' : ''}`}
                        onClick={() => setShowThemeDropdown(!showThemeDropdown)}
                        aria-label="Change Theme"
                        title="Change Theme"
                    >
                        <i className="fa-solid fa-palette"></i>
                    </button>

                    {showThemeDropdown && (
                        <div className="theme-dropdown">
                            <div className={`theme-option ${theme === 'light' ? 'active' : ''}`} onClick={() => handleThemeSelect('light')}>
                                <span className="icon-span"><i className="fa-solid fa-sun"></i></span> Light
                            </div>
                            <div className={`theme-option ${theme === 'ocean' ? 'active' : ''}`} onClick={() => handleThemeSelect('ocean')}>
                                <span className="icon-span"><i className="fa-solid fa-water"></i></span> Ocean
                            </div>
                            <div className={`theme-option ${theme === 'dark' ? 'active' : ''}`} onClick={() => handleThemeSelect('dark')}>
                                <span className="icon-span"><i className="fa-solid fa-moon"></i></span> Dark
                            </div>
                            <div className={`theme-option ${theme === 'sunset' ? 'active' : ''}`} onClick={() => handleThemeSelect('sunset')}>
                                <span className="icon-span"><i className="fa-solid fa-cloud-sun"></i></span> Sunset
                            </div>
                        </div>
                    )}
                </div>

                <button className="logout-button" onClick={handleLogoutClick} title="Logout" aria-label="Logout">
                    <img src="/menuIcons/poweroff.svg" alt="Logout" className="logout-icon" />
                </button>
            </div>

            <div className="content-layout">
                <div className="header-section">
                    <h1 className="title">Choose Your Experience</h1>
                    <p className="subtitle">Select how you'd like to enjoy your order</p>
                </div>

                <div className="cards-container">
                    {isLoading ? (
                        <div className="loading-container">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="loading-text">Synchronizing Workflow...</p>
                        </div>
                    ) : (
                        <>
                            <div className="cards-flex-layout">
                                {visibleButtons.map((btn, index) => (
                                    <div
                                        key={`btn-${index}`}
                                        tabIndex="0"
                                        className="action-card"
                                        onClick={() => handleNavigation(btn.path, btn.type)}
                                    >
                                        <div className="card-icon">
                                            <i className={`fa-solid ${btn.icon}`}></i>
                                        </div>
                                        <h3 className="card-title">{btn.label}</h3>
                                        <p className="card-description">{btn.description}</p>
                                        <div className="card-arrow">
                                            <ArrowRight size={20} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {showLogoutModal && (
                <div className="modal-overlay">
                    <div className="logout-modal">
                        <div className="modal-icon">
                            <i className="fa-solid fa-circle-exclamation"></i>
                        </div>
                        <h2 className="modal-title">Logout Confirmation</h2>
                        <p className="modal-text">Are you sure you want to logout from the system?</p>
                        <div className="modal-buttons">
                            <button className="modal-btn confirm-btn" onClick={confirmLogout}>
                                <i className="fa-solid fa-check"></i> YES
                            </button>
                            <button className="modal-btn cancel-btn" onClick={cancelLogout}>
                                <i className="fa-solid fa-xmark"></i> NO
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FirstTab;
